import { take } from '@execonline-inc/collections';
import { PopoverPlacement, useMediaQuery } from '@execonline-inc/execonline-ui';
import { when } from '@execonline-inc/maybe-adapter';
import { offset } from '@floating-ui/react';
import { fromNullable, just, Maybe, nothing } from 'maybeasy';
import { equals } from 'ramda';
import { ExperienceDetailResource } from '../../../../../../CurrentCollectionStep/BuilderV2/Types';

export const loading = (): Loading => ({ kind: 'loading' });
export const waiting = (): Waiting => ({ kind: 'waiting' });
export const ready = (resource: ExperienceDetailResource): Ready => ({ kind: 'ready', resource });
export const errored = (message: string): Errored => ({ kind: 'errored', message });

export type State = Loading | Errored | Ready | Waiting;

function validateEventTarget(eventTarget: EventTarget) {
  return fromNullable(eventTarget).andThen<HTMLElement>((target) =>
    target instanceof HTMLElement ? just(target) : nothing(),
  );
}

function whenContainsElement(rootElement: HTMLElement, element: HTMLElement): Maybe<{}> {
  return when(equals(element, rootElement) || rootElement.contains(element), {});
}

function whenContainsEventTarget(rootElement: EventTarget, element: EventTarget): Maybe<{}> {
  return just({})
    .assign('rootElement', validateEventTarget(rootElement))
    .assign('element', validateEventTarget(element))
    .andThen(({ element, rootElement }) => whenContainsElement(rootElement, element));
}

export function handleMouseDown(action: () => void) {
  return (e: React.MouseEvent) => {
    whenContainsEventTarget(e.currentTarget, e.target).do(() => {
      e.stopPropagation();
      action();
    });
  };
}

export function handleEnterKeyDown(action: () => void) {
  return (e: React.KeyboardEvent) => {
    whenContainsEventTarget(e.currentTarget, e.target)
      .andThen(() => when(equals(e.key, 'Enter'), {}))
      .do(() => {
        e.stopPropagation();
        action();
      });
  };
}

export const getCompetencies = (competencies: string[], expanded: boolean) => {
  return when(expanded, true)
    .map(() => competencies)
    .getOrElse(() => [...take(4, competencies)]);
};

export const useProgramPopoverPlacement = (): PopoverPlacement => {
  const breakPoint = useMediaQuery();

  switch (breakPoint) {
    case 'xl':
    case 'lg':
    case 'md':
      return 'right';
    case 'sm':
    case 'xs':
      return 'top';
  }
};

interface Errored {
  kind: 'errored';
  message: string;
}

interface Waiting {
  kind: 'waiting';
}
interface Loading {
  kind: 'loading';
}

interface Fetching {
  kind: 'fetching';
}

interface Fetched {
  kind: 'fetched';
}

export type DetailsCacheState = Waiting | Fetching | Fetched;
interface Ready {
  kind: 'ready';
  resource: ExperienceDetailResource;
}

export const calculatePlacementOffset = (placement: string) => {
  switch (placement) {
    case 'top':
      return offset(({ rects }) => -rects.reference.height / 2 - rects.floating.height / 2);
    default:
      return offset(5);
  }
};
